import React, { useEffect, useRef, useState } from "react";
import { PRICEME_AD_PLACEMENT_ATTR, PRICEME_AD_TYPE_ATTR, PricemeAdPlacementType } from "@/ads/priceme-ad-placement";
import { getAdProvider } from "@/ads/getAdProvider";
import config from "@/config";
import {
    FULLSCREEN_STICKY_FOOTER_BOTTOM_SAFE_PADDING_PX,
    STICKY_FOOTER_AD_RESERVED_HEIGHT_PX,
    getFullscreenSafeAreaInsetBottomPx,
} from "@/ads/fullscreen-sticky-footer-layout";

interface AdSenseAdProps {
    /**
     * Ad unit slot ID (data-ad-slot). If omitted, we default to `config.ADSENSE_GENERAL_AD_PLACEMENT_SLOT_ID`.
     */
    slot?: string;
    format?: string;
    /** Optional — used for in-feed ads. */
    adLayoutKey?: string;
    style?: React.CSSProperties;
    className?: string;
    /** Enable test mode to show placeholder/test ads. */
    testMode?: boolean;
    /**
     * For Raptive selectors: provides a stable ad-type marker on the anchor node.
     * Defaults to `"content"`. Use `"sticky-footer"` for the fixed bottom placement.
     */
    placementType?: PricemeAdPlacementType;
}

/** Matches fullscreen / Fancybox sticky-footer anchor height (see `fullscreen-sticky-footer-layout.ts`). */
const stickyFooterAnchorStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    height: `${STICKY_FOOTER_AD_RESERVED_HEIGHT_PX}px`,
    maxHeight: `${STICKY_FOOTER_AD_RESERVED_HEIGHT_PX}px`,
    overflow: "hidden",
};

type AdPlacementCoreProps = AdSenseAdProps;

/**
 * Renders the ad unit anchor (Raptive / AdSense / test placeholder) without the page-level sticky shell.
 */
const AdPlacementCore: React.FC<AdPlacementCoreProps> = ({
    slot,
    format = "auto",
    adLayoutKey,
    style,
    className,
    // Auto-enable test mode on localhost and staging; disable in production.
    testMode = process.env.REACT_APP_STAGE !== "production",
    placementType = "content",
}) => {
    const adRef = useRef<HTMLDivElement>(null);
    const hasInitializedRef = useRef<boolean>(false);
    const provider = getAdProvider();
    const isStickyFooter = placementType === "sticky-footer";

    useEffect(() => {
        // Only initialize AdSense when provider is ADSENSE and not in test mode.
        if (provider !== "ADSENSE" || testMode || hasInitializedRef.current) {
            return;
        }
        try {
            // ALWAYS push, never check if it exists first.
            // This creates the queue if the script hasn't loaded yet.
            // This fixes the race condition on mobile where React loads before adsbygoogle.js finishes loading.
            ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
            hasInitializedRef.current = true; // Mark as initialized to prevent duplicates
        } catch (error) {
            console.error("Error initializing AdSense:", error);
        }
    }, [provider, testMode]);

    // Raptive: empty anchor nodes where inventory is expected (same layout shell as AdSense).
    if (provider === "RAPTIVE") {
        const placementWidthClassName =
            placementType === "header"
                ? "tw-mx-auto tw-max-w-[970px]"
                : placementType === "content" || placementType === "sticky-footer"
                  ? "tw-mx-auto tw-max-w-[728px]"
                  : "";
        const containerClassName = adLayoutKey
            ? `tw-w-full tw-my-4 ${placementWidthClassName} ${className || ""}`
            : `tw-w-full ${placementWidthClassName} ${className || ""}`;
        const baseStyle: React.CSSProperties = isStickyFooter
            ? stickyFooterAnchorStyle
            : adLayoutKey
              ? { display: "block" }
              : { display: "block", width: "100%", minHeight: "250px" };
        const stickySidebarStyle: React.CSSProperties =
            placementType === "sticky-sidebar"
                ? {
                      minWidth: "320px",
                  }
                : {};
        const containerStyle: React.CSSProperties = { ...baseStyle, ...stickySidebarStyle, ...style };
        return (
            <div
                ref={adRef}
                className={isStickyFooter ? `tw-w-full ${className || ""}` : containerClassName}
                style={containerStyle}
                {...{ [PRICEME_AD_PLACEMENT_ATTR]: "" }}
                {...{ [PRICEME_AD_TYPE_ATTR]: placementType }}
            />
        );
    }

    // Test mode: show a placeholder ad box on localhost/staging.
    if (testMode) {
        const placeholderHeight = isStickyFooter
            ? `${STICKY_FOOTER_AD_RESERVED_HEIGHT_PX}px`
            : adLayoutKey
              ? "100px"
              : "250px";
        const containerClassName = isStickyFooter
            ? `tw-w-full ${className || ""}`
            : adLayoutKey
              ? `tw-w-full tw-my-4 ${className || ""}`
              : `tw-w-full tw-flex tw-justify-center tw-my-4 ${className || ""}`;
        return (
            <div className={containerClassName} style={style}>
                <div
                    className="tw-bg-gray-100 tw-border-2 tw-border-dashed tw-border-gray-300 tw-rounded-lg tw-p-4 tw-text-center tw-w-full"
                    style={{
                        minHeight: placeholderHeight,
                        height: isStickyFooter ? placeholderHeight : undefined,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <div className="tw-text-gray-500 tw-text-sm tw-mb-2">AdSense Ad Placeholder</div>
                    <div className="tw-text-gray-400 tw-text-xs">{adLayoutKey ? "In-Feed Ad" : isStickyFooter ? "Sticky Footer" : "300 x 250"}</div>
                    <div className="tw-text-gray-400 tw-text-xs tw-mt-2">Test Mode</div>
                </div>
            </div>
        );
    }

    // Default to the "general ad placement" slot when none is provided.
    const resolvedSlot = slot ?? config.ADSENSE_GENERAL_AD_PLACEMENT_SLOT_ID;
    const adProps: Record<string, unknown> = {
        className: "adsbygoogle",
        style: { display: "block" }, // Match Google's exact implementation for in-feed ads
        "data-ad-client": config.ADSENSE_CLIENT_ID,
        "data-ad-format": format,
    };
    // For non-in-feed ads (like sidebar ads), use responsive styling without max-width constraint.
    // This allows the ad to fit within its container (e.g., 160px/200px for sidebars).
    if (!adLayoutKey) {
        adProps.style = isStickyFooter
            ? { display: "block", width: "100%", minHeight: `${STICKY_FOOTER_AD_RESERVED_HEIGHT_PX}px` }
            : { display: "block", width: "100%", minHeight: "250px" };
    }
    // For in-feed ads, add layout key and remove full-width-responsive.
    if (adLayoutKey) {
        adProps["data-ad-layout-key"] = adLayoutKey;
    } else {
        adProps["data-full-width-responsive"] = "true";
    }
    adProps["data-ad-slot"] = resolvedSlot;
    // For in-feed ads, use a simpler container without centering.
    // For sidebar ads (non-in-feed, no adLayoutKey), don't add centering classes.
    const containerClassName = adLayoutKey ? `tw-w-full tw-my-4 ${className || ""}` : `tw-w-full ${className || ""}`;
    const containerStyle: React.CSSProperties = { ...style, display: "block" };
    return (
        <div ref={adRef} className={isStickyFooter ? `tw-w-full ${className || ""}` : containerClassName} style={containerStyle}>
            <ins {...adProps} />
        </div>
    );
};

type StickyFooterAdShellProps = {
    className?: string;
    children: React.ReactNode;
};

/**
 * Fixed bottom shell for sticky-footer placements (safe area, max width, pointer-events).
 * Layout matches puzzle listing photo fullscreen / Fancybox footer.
 */
const StickyFooterAdShell: React.FC<StickyFooterAdShellProps> = ({ className, children }) => {
    const [safeAreaBottomInsetPx, setSafeAreaBottomInsetPx] = useState<number>(0);
    useEffect(() => {
        const syncSafeAreaInset = (): void => {
            setSafeAreaBottomInsetPx(getFullscreenSafeAreaInsetBottomPx());
        };
        syncSafeAreaInset();
        window.addEventListener("resize", syncSafeAreaInset);
        window.addEventListener("orientationchange", syncSafeAreaInset);
        return () => {
            window.removeEventListener("resize", syncSafeAreaInset);
            window.removeEventListener("orientationchange", syncSafeAreaInset);
        };
    }, []);
    const innerPaddingBottomPx = safeAreaBottomInsetPx + FULLSCREEN_STICKY_FOOTER_BOTTOM_SAFE_PADDING_PX;
    return (
        <div
            className={`priceme-fullscreen-sticky-footer-ad tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[96] tw-pointer-events-none ${className ?? ""}`}
        >
            <div
                className="tw-mx-auto tw-max-w-[728px] tw-px-3 tw-pointer-events-auto"
                style={{ paddingBottom: `${innerPaddingBottomPx}px` }}
            >
                {children}
            </div>
        </div>
    );
};

/**
 * AdSense / Raptive placement component.
 * Use `placementType="sticky-footer"` for the fixed bottom bar; defaults to in-content placement.
 *
 * @param props - Component props
 * @returns Ad placement element, or null when ads are disabled
 */
const AdSenseAd: React.FC<AdSenseAdProps> = (props) => {
    const { placementType = "content", className } = props;
    const provider = getAdProvider();

    if (provider === "OFF") {
        return null;
    }

    if (placementType === "sticky-footer") {
        return (
            <StickyFooterAdShell className={className}>
                <AdPlacementCore {...props} />
            </StickyFooterAdShell>
        );
    }

    return <AdPlacementCore {...props} />;
};

export default AdSenseAd;
